import { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Send, Mail, ArrowRight, Facebook, Instagram, Youtube, Linkedin } from "lucide-react";
import { Input } from "../ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { legalFooterLabels } from "../../data/legalContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/pdq87uby_cropped-LOGOS-IR-1.png";

const translations = {
  newsletterTitle: { es: "Mantente informado", en: "Stay Informed" },
  newsletterSubtitle: {
    es: "Recibe novedades sobre nuevos cursos, tendencias de la industria y contenido exclusivo de nuestra academia.",
    en: "Receive news about new courses, industry trends, and exclusive content from our academy.",
  },
  emailPlaceholder: { es: "Tu correo electrónico", en: "Your email address" },
  subscribe: { es: "Suscribirse", en: "Subscribe" },
  footerDescription: {
    es: "Academia especializada en la industria de reuniones (MICE) para profesionales de Latinoamérica.",
    en: "Specialized academy in the meetings industry (MICE) for Latin American professionals.",
  },
  viewCourses: { es: "Ver cursos", en: "View Courses" },
  quickLinks: { es: "Enlaces rápidos", en: "Quick Links" },
  contact: { es: "Contacto", en: "Contact" },
  links: {
    es: [
      { label: "Cursos", href: "#cursos" },
      { label: "Beneficios", href: "#beneficios" },
      { label: "Testimonios", href: "#testimonios" },
      { label: "Preguntas frecuentes", href: "#faq" },
      { label: "Sobre nosotros", href: "#about" },
    ],
    en: [
      { label: "Courses", href: "#cursos" },
      { label: "Benefits", href: "#beneficios" },
      { label: "Testimonials", href: "#testimonios" },
      { label: "FAQ", href: "#faq" },
      { label: "About Us", href: "#about" },
    ],
  },
  copyright: { es: "Todos los derechos reservados.", en: "All rights reserved." },
  errorEmail: { es: "Por favor ingresa tu correo electrónico", en: "Please enter your email address" },
  errorGeneric: { es: "Error al suscribirse. Intenta de nuevo.", en: "Subscription error. Please try again." },
};

const FooterSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const links = t(translations.links);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t(translations.errorEmail));
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/subscribe`, { email, name: null, source: "landing_page_footer" });
      if (response.data.success) {
        toast.success(response.data.message);
        setEmail("");
      }
    } catch (error) {
      const message = error.response?.data?.detail || t(translations.errorGeneric);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid="footer-section" ref={ref} className="bg-[#0B1F3B] relative overflow-hidden">
      <div className="py-20 md:py-24 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
            <div className="w-14 h-14 bg-[#2ED3C6]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-7 h-7 text-[#2ED3C6]" />
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-white font-medium tracking-tight mb-4">{t(translations.newsletterTitle)}</h2>
            <p className="font-body text-white/60 text-base md:text-lg max-w-lg mx-auto mb-8">{t(translations.newsletterSubtitle)}</p>
            <form onSubmit={handleSubmit} data-testid="email-capture-form" className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder={t(translations.emailPlaceholder)}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                  className="w-full bg-white/10 border-white/20 text-white placeholder:text-white/40 h-12 font-body"
                  disabled={isLoading}
                />
              </div>
              <button
                type="submit"
                data-testid="subscribe-button"
                disabled={isLoading}
                className="inline-flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-6 h-12 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" /> : <><span>{t(translations.subscribe)}</span><Send className="w-4 h-4" /></>}
              </button>
            </form>
          </motion.div>
        </div>
      </div>

      <div className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            <div className="md:col-span-1">
              <div className="inline-block bg-white/95 rounded-lg px-3 py-2 mb-6">
                <img src={LOGO_URL} alt="Industria de Reuniones — academia MICE LATAM" width="120" height="32" decoding="async" className="h-8" />
              </div>
              <p className="font-body text-white/60 text-sm leading-relaxed mb-6">{t(translations.footerDescription)}</p>
              <button onClick={() => scrollToSection('#cursos')} data-testid="footer-cta" className="inline-flex items-center gap-2 text-[#F59E0B] font-body font-semibold text-sm hover:text-[#D97706] transition-colors group">
                {t(translations.viewCourses)}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            <div>
              <h3 className="font-display text-white font-medium mb-4">{t(translations.quickLinks)}</h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.href}>
                    <button onClick={() => scrollToSection(link.href)} className="font-body text-white/60 text-sm hover:text-[#2ED3C6] transition-colors">
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-display text-white font-medium mb-4">{t(translations.contact)}</h3>
              <ul className="space-y-3 font-body text-white/60 text-sm">
                <li>Latinoamérica</li>
                <li>mariana@incubatour.com</li>
                <li>
                  <a href="tel:+525619307330" data-testid="footer-phone" className="hover:text-[#2ED3C6] transition-colors">
                    +52 56 1930 7330
                  </a>
                </li>
              </ul>
              <div className="flex items-center gap-3 mt-6 flex-wrap" data-testid="social-links">
                {[
                  {
                    name: "Facebook",
                    href: "https://www.facebook.com/incubatour/?locale=es_LA",
                    icon: <Facebook className="w-4 h-4" strokeWidth={2} />,
                  },
                  {
                    name: "Instagram",
                    href: "https://www.instagram.com/incubatour/?hl=es",
                    icon: <Instagram className="w-4 h-4" strokeWidth={2} />,
                  },
                  {
                    name: "LinkedIn",
                    href: "https://www.linkedin.com/company/incubatour/?originalSubdomain=mx",
                    icon: <Linkedin className="w-4 h-4" strokeWidth={2} />,
                  },
                  {
                    name: "YouTube",
                    href: "https://www.youtube.com/@conexionturismo",
                    icon: <Youtube className="w-4 h-4" strokeWidth={2} />,
                  },
                  {
                    name: "TikTok",
                    href: "https://www.tiktok.com/@incubatour",
                    icon: (
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
                      </svg>
                    ),
                  },
                  {
                    name: "Spotify",
                    href: "https://open.spotify.com/show/6mLqBjQl7AdCqR1Th9OJrm",
                    icon: (
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z" />
                      </svg>
                    ),
                  },
                ].map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Incubatour en ${social.name}`}
                    data-testid={`social-${social.name.toLowerCase()}`}
                    className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center text-white/60 hover:bg-[#2ED3C6] hover:text-[#0B1F3B] transition-all duration-300 hover:-translate-y-0.5"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-body text-white/40 text-sm text-center md:text-left">© {new Date().getFullYear()} Industria de Reuniones. {t(translations.copyright)}</p>
            <nav data-testid="legal-links" className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-body text-white/50 text-sm">
              <Link to="/legal/privacy" data-testid="footer-privacy-link" className="hover:text-[#2ED3C6] transition-colors">{t(legalFooterLabels.privacy)}</Link>
              <span className="text-white/20">·</span>
              <Link to="/legal/cookies" data-testid="footer-cookies-link" className="hover:text-[#2ED3C6] transition-colors">{t(legalFooterLabels.cookies)}</Link>
              <span className="text-white/20">·</span>
              <Link to="/legal/terms" data-testid="footer-terms-link" className="hover:text-[#2ED3C6] transition-colors">{t(legalFooterLabels.terms)}</Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;
